import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import {
  ProjectSpendingDataCollectionDocument,
  ProjectSpendingDataFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { getF1StatusColor } from 'modules/expenses/lib/expensables/statusF1Color'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import { IProjectSpendingPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SpendingPage/types'

interface ProjectSpendingDataCollectionProps {
  openSidePanel: (newData: IProjectSpendingPanelData) => void
  projectId: number
  projectCurrency: string
}

const ProjectSpendingDataCollection = ({
  openSidePanel,
  projectId,
  projectCurrency,
}: ProjectSpendingDataCollectionProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const itemOnClick = (item: ProjectSpendingDataFragment) => () => {
    if (!item.expensable || !item.expensable.owner) return
    openSidePanel({
      effectiveOn: item.effectiveOn,
      employeeName: item.expensable.owner.access.fullName,
      exchangeRate: item.exchangeRate,
      expensableId: item.expensable.id,
      legalEntityLegalName: item.expensable.owner.legalEntity?.legalName,
      legalEntityAmountCents: Number(item.legalEntityAmountCents),
      legalEntityAmountCurrency: item.legalEntityAmountCurrency,
      originalAmountCents: item.originalAmountCents || 0,
      originalAmountCurrency: item.originalAmountCurrency || projectCurrency,
    })
  }

  const source = useGraphqlDataSource({
    document: ProjectSpendingDataCollectionDocument,
    mapData: (data) => data.projectManagement.expenseRecordsConnection.nodes,
    mapVariables: ({ search, sortings }) => ({
      employeeUserNameLike: search,
      projectId,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.projectManagement.expenseRecordsConnection.totalCount ?? 0,
    }),
    itemOnClick,
    search: {
      enabled: true,
    },
    sortings: {
      legalEntityAmountCents: {
        label: i18n.t('project_management.spending.table.headers.amount'),
      },
      effectiveOn: {
        label: i18n.t('project_management.spending.table.headers.document_date'),
      },
      status: {
        label: i18n.t('project_management.spending.table.headers.status'),
      },
    },
    defaultSorting: {
      field: 'effectiveOn',
      order: 'desc',
    },
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('project_management.spending.table.headers.employee'),
                render: (item) => {
                  const owner = item.expensable?.owner
                  return {
                    type: 'person',
                    value: {
                      firstName: owner?.access?.firstName || '',
                      lastName: owner?.access?.lastName || '',
                      src: buildUrl(owner?.access?.avatar),
                    },
                  }
                },
              },
              {
                label: i18n.t('project_management.spending.table.headers.document_date'),
                render: (item) => ({
                  type: 'date',
                  value: item.effectiveOn ? new Date(item.effectiveOn) : undefined,
                }),
                sorting: 'effectiveOn',
              },
              {
                label: i18n.t('project_management.spending.table.headers.amount'),
                render: (item) => {
                  const amount = Number(item.legalEntityAmountCents) || item.originalAmountCents
                  const currency =
                    item.legalEntityAmountCurrency || item.originalAmountCurrency || projectCurrency

                  return {
                    type: 'text',
                    value:
                      amount && isCurrency(currency)
                        ? MoneyWithCurrency({
                            amount,
                            currency: currency,
                            optionalDecimals: true,
                            locale,
                          })
                        : undefined,
                  }
                },
                sorting: 'legalEntityAmountCents',
              },
              {
                label: i18n.t('project_management.spending.table.headers.subproject'),
                render: (item) => {
                  const subproject = item.subproject
                  if (!subproject) return
                  return {
                    type: 'text',
                    value: subproject.name,
                  }
                },
              },
              {
                label: i18n.t('project_management.spending.table.headers.category'),
                render: (item) => {
                  const category =
                    item.expensable?.expense?.category || item.expensable?.mileage?.category
                  if (!category) return
                  return {
                    type: 'tag',
                    value: {
                      label: category.name,
                    },
                  }
                },
              },
              {
                label: i18n.t('project_management.spending.table.headers.status'),
                render: (item) => {
                  const status = item.expensable?.status
                  if (!status) return undefined

                  return {
                    type: 'dotTag',
                    value: {
                      label: getStatusLabel(status),
                      color: getF1StatusColor(status),
                    },
                  }
                },
                sorting: 'status',
              },
            ],
          },
        },
      ]}
    />
  )
}

export default ProjectSpendingDataCollection
